import React, { useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import { useIntl } from "react-intl";
import styles from "./style.module.scss";

const CircularProgressBar = (props) => {
  const intl = useIntl();
  const radius = (props.size - props.strokeWidth) / 2;
  const viewBox = `0 0 ${props.size} ${props.size}`;
  const dashArray = radius * Math.PI * 2;
  const dashOffset =
    dashArray - (dashArray * props.currentProgress) / props.maxProgress;
  const [showTooltip, setShowTooltip] = useState(false);
  const [textWidth, setTextWidth] = useState(0);
  const textRef = useRef(null);

  // Filter out locked items from the menuList, but only consider visible items
  const visibleItems = props.menuList.filter((item) => item.visible !== false);
  const lockedItems = visibleItems.filter((item) => item.locked === true);

  // Track completion announcements for screen readers
  const [announcement, setAnnouncement] = useState("");
  const previouslyCompleted = useRef(false);

  useEffect(() => {
    if (textRef.current) {
      const bbox = textRef.current.getBBox();
      setTextWidth(bbox.width);
    }
  }, [props.currentProgress]);

  // Announce when overall progress is completed
  useEffect(() => {
    const isCompleted =
      props.maxProgress > 0 &&
      props.currentProgress === props.maxProgress &&
      lockedItems.length === 0;

    if (isCompleted && !previouslyCompleted.current) {
      const message = intl.formatMessage({
        id: "Progress complete",
        defaultMessage: "Progress complete",
        description: "Announcement when all progress is completed",
      });

      setAnnouncement(message);
      previouslyCompleted.current = true;
    } else if (!isCompleted) {
      previouslyCompleted.current = false;
    }
  }, [props.currentProgress, props.maxProgress, lockedItems.length, intl]);

  const getAriaLabel = () => {
    if (props.ariaLabel) {
      return props.ariaLabel;
    }

    if (lockedItems.length > 0) {
      return intl.formatMessage(
        {
          id: "{current} completed, some sections locked",
          defaultMessage: "{current} completed, some sections locked",
          description: "Progress indicator with locked sections",
        },
        { current: props.currentProgress }
      );
    }

    return intl.formatMessage(
      {
        id: "{current} of {max} completed",
        defaultMessage: "{current} of {max} completed",
        description: "Progress indicator",
      },
      {
        current: props.currentProgress,
        max: props.maxProgress,
      }
    );
  };

  return (
    <div>
      {/* Screen reader announcement - only has content during state transitions */}
      <div
        role="status"
        aria-live="polite"
        aria-atomic="true"
        className={styles.srOnly}
      >
        {announcement}
      </div>

      <svg
        width={props.size}
        height={props.size}
        viewBox={viewBox}
        role="img"
        aria-label={getAriaLabel()}
      >
        <circle
          fill="none"
          stroke="#fff"
          cx={props.size / 2}
          cy={props.size / 2}
          r={radius}
          strokeWidth={`${props.strokeWidth}px`}
          aria-hidden="true"
        />
        <circle
          className={styles.progressStroke}
          fill="none"
          stroke={props.stroke}
          strokeDasharray={dashArray}
          strokeDashoffset={dashOffset}
          cx={props.size / 2}
          cy={props.size / 2}
          r={radius}
          strokeWidth={`${props.strokeWidth}px`}
          transform={`rotate(-90 ${props.size / 2} ${props.size / 2})`}
          aria-hidden="true"
        />

        {/* Centered group for text + lock/max, always centered as a group */}
        <g
          transform={`translate(${props.size / 2}, ${props.size / 2})`}
          aria-hidden="true"
        >
          {lockedItems.length > 0 ? (
            <g textAnchor="middle" dominantBaseline="middle">
              {/* Center the group by shifting left by half the total width */}
              <g transform={`translate(${-((textWidth + 15) / 2)},0)`}>
                <text
                  ref={textRef}
                  fill="#fff"
                  y={2}
                  className={styles.progressBarText}
                  textAnchor="start"
                  dominantBaseline="middle"
                  alignmentBaseline="middle"
                >
                  {props.currentProgress}/
                </text>
                <g
                  transform={`translate(${textWidth + 2}, -7.5)`}
                  onMouseEnter={() => setShowTooltip(true)}
                  onMouseLeave={() => setShowTooltip(false)}
                >
                  <svg width="15" height="15" viewBox="0 0 15 15">
                    <path
                      d="M7.49957 0C10.4996 1.96172e-08 12.0709 2 12.0709 4.5V5.71484H13.357C13.9093 5.71484 14.357 6.16256 14.357 6.71484V13.8574C14.3568 14.4096 13.9092 14.8574 13.357 14.8574H1.64313C1.09094 14.8574 0.643278 14.4096 0.643127 13.8574V6.71484C0.64313 6.16256 1.09084 5.71484 1.64313 5.71484H2.92828V4.5C2.92828 2.00012 4.49985 0.000186365 7.49957 0ZM7.49957 9.14258C6.86858 9.1428 6.35699 9.65509 6.35699 10.2861C6.35722 10.917 6.86872 11.4285 7.49957 11.4287C8.13062 11.4287 8.6429 10.9171 8.64313 10.2861C8.64313 9.65495 8.13075 9.14258 7.49957 9.14258ZM7.49957 2.28613C6.49967 2.28635 5.2145 3.07159 5.21442 4.57129V5.71484H9.78571V4.5C9.78571 3 8.49957 2.28613 7.49957 2.28613Z"
                      fill="white"
                    />
                  </svg>
                </g>
              </g>
            </g>
          ) : (
            // Unlocked: single text node so tests see "current/max"
            <text
              className={styles.progressBarText}
              fill="#fff"
              textAnchor="middle"
              dominantBaseline="middle"
            >
              {`${props.currentProgress}/${props.maxProgress}`}
            </text>
          )}
        </g>
      </svg>
      {lockedItems.length > 0 && showTooltip && (
        <div className={styles.tooltip}>
          Total progress will be visible when you unlock all sections
        </div>
      )}
    </div>
  );
};

export default CircularProgressBar;

CircularProgressBar.propTypes = {
  /**
   * Size of circular progress bar
   */
  size: PropTypes.number,
  /**
   * Maximum value of progress
   */
  maxProgress: PropTypes.number,
  /**
   * Current Progress value of progress
   */
  currentProgress: PropTypes.number,
  /**
   * Width of progress stroke
   */
  strokeWidth: PropTypes.number,
  /**
   * Colour of progress stroke [hex code or colour name]
   */
  ariaLabel: PropTypes.string,
};

CircularProgressBar.defaultProps = {
  size: 80,
  value: 0,
  maxProgress: 100,
  currentProgress: 0,
  strokeWidth: 10,
  stroke: "white",
};
